import { MiningMinigame, ORE_TIMEOUT } from '@phaser/minigames/mining';
import { sendAppMessage } from '@helpers/mobileApp';
import { Ore } from '@phaser/minigames/mining/Ore';
import { playShot } from '@components/sound/Sound';
import { DEPTH } from '@phaser/data/depth';
import { Game } from '@phaser/scenes/Game';

export const BAR_WIDTH = 132;
export const BAR_HEIGHT = 16;

export class Indicator {
  private _scene = Game.current;
  private _mining: MiningMinigame;

  private _tween: Phaser.Tweens.Tween;
  private _container: Phaser.GameObjects.Container;
  private _hits: Phaser.GameObjects.Arc[] = [];
  private _hitPos: Phaser.GameObjects.Rectangle;

  private _currentPos = 0;
  private _currentHit = 0;
  private _hitWidth = BAR_WIDTH * 0.75;

  constructor(
    mining: MiningMinigame,
    ore: Ore,
  ) {
    this._mining = mining;

    this._placeIndicator(ore.x, ore.y);
    this._placeHits(ore.strength);
    this._placeTimer(ore.spawnedAt);
  }

  public destroy() {
    if (!this._container) return;

    this._container.destroy();
    this._tween.stop();
  }

  public mine() {
    if (!this._container || !this._hitPos) return;

    const xPos = this._hitPos.x - this._hitWidth / 2;
    const xPosEnd = this._hitPos.x + this._hitWidth / 2;

    if (this._currentPos >= xPos && this._currentPos <= xPosEnd) {
      this._scene.cameras.main.shake(50, 0.005 / window.devicePixelRatio);

      playShot();
      sendAppMessage('vibrate');

      this._hits[this._currentHit].setFillStyle(0xFFFFFF);
      this._scene.add.tween({
        targets: this._hits[this._currentHit],
        scale: { from: 2, to: 1 },
        ease: 'Power2',
        duration: 300,
      });
      this._currentHit++;

      if (this._currentHit >= this._hits.length) {
        return this._mining.collectOre();
      }

      this._hitWidth = this._hitWidth / 1.75 * this._mining.difficulty;
    } else {
      this._currentHit = 0;
      this._hits.forEach(hit => hit.setFillStyle(0x0));

      this._hitWidth = BAR_WIDTH * 0.75;
    }

    const maxHitPos = BAR_WIDTH / 2 - this._hitWidth / 2;
    const minHitPos = -BAR_WIDTH / 2 + this._hitWidth / 2;

    this._hitPos
      .setX(Math.floor(Math.random() * (maxHitPos - minHitPos + 1) + minHitPos))
      .setSize(this._hitWidth, BAR_HEIGHT);
  }

  private _placeIndicator(x: number, y: number) {
    this._container = this._scene.add.container(x, y - 64)
      .setDepth(DEPTH.MINIGAME + 1);

    const white = this._scene.add.rectangle(0, 0, BAR_WIDTH, BAR_HEIGHT, 0xFFFFFF, 0.2);
    this._hitPos = this._scene.add.rectangle(0, 0, this._hitWidth, BAR_HEIGHT, 0xBCEDFF);
    const indicator = this._scene.add.rectangle(-BAR_WIDTH / 2, 0, 4, BAR_HEIGHT + 4, 0xFFFFFF)
      .setStrokeStyle(2, 0x0);

    this._container.add([white, this._hitPos, indicator]);

    this._tween = this._scene.add.tween({
      targets: indicator,
      x: BAR_WIDTH / 2,
      duration: 1000,
      ease: 'Sine.easeInOut',
      repeat: -1,
      yoyo: true,
      onUpdate: (tween) => {
        const value = tween.getValue();
        this._currentPos = value;
      }
    });
  }

  private _placeHits(strength: number) {
    Array.from({ length: strength }, (_, i) => {
      const hit = this._scene.add
        .circle(-BAR_WIDTH / 2 + 4 + i * 14, -28, 4, 0x0)
        .setStrokeStyle(1, 0xFFFFFF);
      this._container.add(hit);
      this._hits.push(hit);
    });
  }

  private _placeTimer(spawnedAt: number) {
    const timer = this._scene.add
      .rectangle(-BAR_WIDTH / 2, -14, BAR_WIDTH, 4, 0xFFFFFF)
      .setOrigin(0, 0.5);
    this._container.add(timer);


    const duration = ORE_TIMEOUT * this._mining.difficulty;
    this._scene.add.tween({
      targets: timer,
      scaleX: 0,
      duration: spawnedAt - Date.now() + duration,
    });
  }
}